import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';

/* ── Status badge ── */
export function OrderStatusBadge({ status }) {
  const map = {
    pending:'badge-warning', confirmed:'badge-info', processing:'badge-info',
    shipped:'badge-success', delivered:'badge-success',
    cancelled:'badge-danger', refunded:'badge-danger',
  };
  return <span className={`badge ${map[status] || 'badge-muted'}`}>{status}</span>;
}

/* ── Stat card ── */
function StatCard({ label, value, sub, delta, icon }) {
  return (
    <div className="dash-stat-card" style={{
      background:'var(--surface)', border:'1px solid var(--border)',
      borderRadius:'var(--radius)', padding:'22px 24px',
      position:'relative', overflow:'hidden',
      boxShadow:'var(--shadow-sm)', transition:'var(--transition)',
    }}
    onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-2px)';e.currentTarget.style.borderColor='var(--border2)';}}
    onMouseLeave={e=>{e.currentTarget.style.transform='';e.currentTarget.style.borderColor='var(--border)';}}>
      <div style={{
        position:'absolute', top:0, right:0, width:120, height:120,
        background:'radial-gradient(circle at 70% 30%, rgba(201,169,110,0.12), transparent 60%)',
        pointerEvents:'none',
      }} />
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
        <p className="dash-stat-label" style={{ fontSize:10.5, letterSpacing:'1.5px', textTransform:'uppercase', color:'var(--muted)', fontFamily:'var(--font-sans)', fontWeight:500, lineHeight:1.4 }}>{label}</p>
        <span className="dash-stat-icon" style={{ fontSize:18, opacity:0.6, flexShrink:0, marginLeft:8 }}>{icon}</span>
      </div>
      <p className="dash-stat-value" style={{ fontFamily:'var(--font-serif)', fontWeight:300, fontSize:34, color:'var(--white)', lineHeight:1, fontVariantNumeric:'lining-nums tabular-nums' }}>
        {value}
      </p>
      {(sub || delta) && (
        <p style={{ fontSize:12, color:'var(--muted)', marginTop:8, display:'flex', alignItems:'center', gap:6, fontFamily:'var(--font-sans)', flexWrap:'wrap' }}>
          {delta && <span style={{ color:'var(--success)', fontWeight:500 }}>↑ {delta}</span>}
          {sub}
        </p>
      )}
    </div>
  );
}

/* ── Bar chart ── */
function BarChart({ data }) {
  if (!data?.length) return null;
  const max = Math.max(...data.map(d => d.revenue || 0), 1);
  return (
    <div>
      <div style={{ display:'flex', alignItems:'flex-end', gap:5, height:120, marginTop:8 }}>
        {data.slice(-12).map((d, i) => {
          const isCurrent = i === data.slice(-12).length - 1;
          return (
            <div key={i} title={`${d.month}: $${Number(d.revenue||0).toLocaleString()}`}
              style={{
                flex:1, minHeight:6, borderRadius:'3px 3px 0 0',
                background: isCurrent
                  ? 'linear-gradient(180deg, #e3c98c, #7a5d2c)'
                  : 'linear-gradient(180deg, #d8e2f3, #b8c8e8)',
                height:`${Math.max(5, ((d.revenue||0)/max)*100)}%`,
                transition:'transform 0.2s',
                cursor:'pointer',
              }}
              onMouseEnter={e=>e.currentTarget.style.transform='scaleY(1.04)'}
              onMouseLeave={e=>e.currentTarget.style.transform=''}
            />
          );
        })}
      </div>
      <div style={{ display:'flex', justifyContent:'space-between', marginTop:8, padding:'0 2px' }}>
        {data.slice(-6).map(d => (
          <span key={d.month} style={{ fontSize:10, color:'var(--muted2)', fontFamily:'var(--font-sans)', letterSpacing:'0.5px' }}>
            {d.month?.slice(5)}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ── Quick action card ── */
function QuickCard({ to, label, desc, icon }) {
  return (
    <Link to={to} style={{
      display:'block', padding:20,
      background:'var(--surface)', border:'1px solid var(--border)',
      borderRadius:'var(--radius)', transition:'var(--transition)', textDecoration:'none',
      boxShadow:'var(--shadow-sm)',
    }}
    onMouseEnter={e=>{ e.currentTarget.style.borderColor='rgba(201,169,110,0.35)'; e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='var(--shadow)'; }}
    onMouseLeave={e=>{ e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow='var(--shadow-sm)'; }}>
      <div style={{ fontSize:22, marginBottom:10 }}>{icon}</div>
      <p style={{ fontSize:13, color:'var(--white)', fontWeight:500, marginBottom:4, fontFamily:'var(--font-sans)' }}>{label}</p>
      <p style={{ fontSize:12, color:'var(--muted)', fontFamily:'var(--font-sans)' }}>{desc}</p>
    </Link>
  );
}

export default function Dashboard() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/orders/dashboard')
      .then(r => setData(r.data.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:20 }}>
      {[...Array(8)].map((_, i) => <div key={i} className="skeleton" style={{ height:130, borderRadius:'var(--radius)' }} />)}
    </div>
  );

  const { revenue, customers, gems, newEnquiries, recentOrders, topGems, monthlyRevenue } = data || {};

  return (
    <div style={{ fontFamily:'var(--font-sans)' }}>
      {/* Page header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:32 }}>
        <div>
          <h1 style={{ fontFamily:'var(--font-serif)', fontSize:34, fontWeight:400, letterSpacing:'-0.5px', color:'var(--white)', marginBottom:4, margin:0 }}>
            Welcome back, <em style={{ fontStyle:'italic', color:'var(--gold-dark)' }}>Ceylon.</em>
          </h1>
          <p style={{ fontSize:13, color:'var(--muted)', marginTop:6 }}>
            {new Date().toLocaleDateString('en-US', { weekday:'long', year:'numeric', month:'long', day:'numeric' })}
          </p>
        </div>
        <Link to="/admin/gems/new" className="btn btn-gold">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Add Gem
        </Link>
      </div>

      {/* Stats row 1 */}
      <div className="dash-stat-grid" style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:20, marginBottom:20 }}>
        <StatCard label="Total Revenue"  value={`$${Number(revenue?.total_revenue||0).toLocaleString()}`} sub="All time · USD" delta="12.4%" icon="💰" />
        <StatCard label="Total Orders"   value={Number(revenue?.total_orders||0).toLocaleString()} sub={`${revenue?.pending||0} pending`} icon="📦" />
        <StatCard label="Customers"      value={Number(customers||0).toLocaleString()} sub="Registered buyers" icon="👥" />
        <StatCard label="New Enquiries"  value={Number(newEnquiries||0).toLocaleString()} sub="Awaiting reply" icon="✉️" />
      </div>

      {/* Stats row 2 */}
      <div className="dash-stat-grid" style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:20, marginBottom:32 }}>
        <StatCard label="Today's Orders"  value={Number(revenue?.orders_today||0).toLocaleString()} sub="Last 24 hours" icon="📅" />
        <StatCard label="Today's Revenue" value={`$${Number(revenue?.revenue_today||0).toLocaleString()}`} sub="Last 24 hours" icon="📈" />
        <StatCard label="Avg Order Value" value={`$${Number(revenue?.avg_order||0).toFixed(0)}`} sub="Per order" icon="🧮" />
        <StatCard label="Active Gems"     value={Number(gems||0).toLocaleString()} sub="In collection" icon="💎" />
      </div>

      {/* Charts row */}
      <div className="dash-chart-grid" style={{ display:'grid', gridTemplateColumns:'1.4fr 1fr', gap:24, marginBottom:24 }}>
        {/* Revenue chart */}
        <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:28, boxShadow:'var(--shadow-sm)' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
            <div>
              <p style={{ fontSize:10.5, letterSpacing:'1.5px', textTransform:'uppercase', color:'var(--muted)', marginBottom:4, fontWeight:500 }}>Revenue</p>
              <h3 style={{ fontFamily:'var(--font-serif)', fontSize:20, fontWeight:400, color:'var(--white)', margin:0 }}>12-Month Overview</h3>
            </div>
            <span style={{ fontSize:11, color:'var(--muted)', fontFamily:'var(--font-sans)' }}>USD</span>
          </div>
          <BarChart data={monthlyRevenue} />
        </div>

        {/* Top gems */}
        <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:28, boxShadow:'var(--shadow-sm)' }}>
          <p style={{ fontSize:10.5, letterSpacing:'1.5px', textTransform:'uppercase', color:'var(--muted)', marginBottom:4, fontWeight:500 }}>Best Sellers</p>
          <h3 style={{ fontFamily:'var(--font-serif)', fontSize:20, fontWeight:400, color:'var(--white)', margin:'0 0 20px' }}>Top Selling Gems</h3>
          {topGems?.length ? topGems.map((g, i) => (
            <div key={i} style={{
              display:'flex', justifyContent:'space-between', alignItems:'center',
              padding:'10px 0', borderBottom: i < topGems.length-1 ? '1px solid var(--border)' : 'none',
            }}>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <div style={{
                  width:26, height:26, borderRadius:'50%', flexShrink:0,
                  background:'linear-gradient(180deg, #e3c98c, #7a5d2c)',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:11, color:'#0c0c10', fontWeight:700, fontFamily:'var(--font-serif)',
                }}>{i+1}</div>
                <div>
                  <p style={{ fontSize:13, color:'var(--white)', fontWeight:500, fontFamily:'var(--font-sans)' }}>{g.name}</p>
                  <p style={{ fontSize:11, color:'var(--muted)' }}>{g.category_name}</p>
                </div>
              </div>
              <div style={{ textAlign:'right' }}>
                <p style={{ fontSize:13, color:'var(--gold)', fontWeight:500, fontFamily:'var(--font-sans)' }}>${Number(g.price_usd||0).toLocaleString()}</p>
                <p style={{ fontSize:11, color:'var(--muted)' }}>{g.sold} sold</p>
              </div>
            </div>
          )) : <p style={{ fontSize:13, color:'var(--muted)' }}>No sales yet</p>}
        </div>
      </div>

      {/* Recent orders */}
      <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:28, marginBottom:24, boxShadow:'var(--shadow-sm)' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:22 }}>
          <div>
            <p style={{ fontSize:10.5, letterSpacing:'1.5px', textTransform:'uppercase', color:'var(--muted)', marginBottom:4, fontWeight:500 }}>Activity</p>
            <h3 style={{ fontFamily:'var(--font-serif)', fontSize:20, fontWeight:400, color:'var(--white)', margin:0 }}>Recent Orders</h3>
          </div>
          <Link to="/admin/orders" className="btn btn-outline btn-sm">View all</Link>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Order #</th><th>Customer</th><th>Total</th><th>Status</th><th>Date</th><th></th>
              </tr>
            </thead>
            <tbody>
              {recentOrders?.length ? recentOrders.map(o => (
                <tr key={o.order_number} style={{ cursor:'pointer' }}>
                  <td style={{ fontFamily:'var(--font-serif)', fontSize:14, color:'var(--gold)', fontWeight:400 }}>{o.order_number}</td>
                  <td style={{ color:'var(--white)', fontWeight:500 }}>{o.customer_name}</td>
                  <td style={{ color:'var(--white)', fontWeight:500 }}>${Number(o.total_usd).toLocaleString()}</td>
                  <td><OrderStatusBadge status={o.status} /></td>
                  <td style={{ color:'var(--muted)', fontSize:12 }}>{new Date(o.created_at).toLocaleDateString()}</td>
                  <td>
                    <Link to={`/admin/orders/${o.id}`} style={{ fontSize:12, color:'var(--gold)', fontFamily:'var(--font-sans)' }}>
                      View →
                    </Link>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={6} style={{ textAlign:'center', color:'var(--muted)', padding:'40px 0' }}>No orders yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick actions */}
      <div className="dash-quick-grid" style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16 }}>
        <QuickCard to="/admin/gems/new"  label="Add New Gem"    desc="Add a gem to the collection" icon="💎" />
        <QuickCard to="/admin/enquiries" label="View Enquiries" desc={`${newEnquiries||0} new messages`} icon="✉️" />
        <QuickCard to="/admin/seo"       label="Manage SEO"     desc="Edit meta tags & analytics" icon="🔍" />
        <QuickCard to="/admin/sliders"   label="Edit Sliders"   desc="Update hero images" icon="🖼️" />
      </div>
    </div>
  );
}
